/**
 * Dot Grid Warp — native Canvas 2D. The overlay is fixed and non-interactive,
 * but its grid is rendered ONLY within the visible Header and Footer bounds.
 * No cursor styles are changed and no animation libraries are used.
 */
const GAP = 26
const REACH = 150
const PUSH = 26
const DOT_DIAMETER = 1
const WHITE = '#FFFFFF'
const MAX_DPR = 2
const SCOPE_SELECTOR = '.site-header, .paye-footer'
const FINE_POINTER = '(hover: hover) and (pointer: fine)'
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

function mountDotGridWarp() {
  const finePointer = window.matchMedia(FINE_POINTER)
  const reducedMotion = window.matchMedia(REDUCED_MOTION)

  /** @type {null | (() => void)} */
  let unmount = null

  function reconcile() {
    const enabled = finePointer.matches && !reducedMotion.matches
    if (enabled && !unmount) unmount = mountCanvas()
    if (!enabled && unmount) {
      unmount()
      unmount = null
    }
  }

  finePointer.addEventListener('change', reconcile)
  reducedMotion.addEventListener('change', reconcile)
  reconcile()

  return () => {
    finePointer.removeEventListener('change', reconcile)
    reducedMotion.removeEventListener('change', reconcile)
    unmount?.()
    unmount = null
  }
}

function mountCanvas() {
  const layer = document.createElement('div')
  layer.className = 'dot-grid-warp'
  layer.setAttribute('aria-hidden', 'true')

  const canvas = document.createElement('canvas')
  canvas.className = 'dot-grid-warp__canvas'
  canvas.setAttribute('aria-hidden', 'true')
  layer.appendChild(canvas)

  const context = canvas.getContext('2d', { alpha: true })
  if (!context) return () => {}

  document.body.appendChild(layer)

  let width = 0
  let height = 0
  let frame = 0
  let destroyed = false
  /** @type {Element[]} */
  let scopes = []

  const pointer = {
    x: 0,
    y: 0,
    targetX: 0,
    targetY: 0,
    strength: 0,
    targetStrength: 0,
    hasPosition: false,
    inViewport: false,
    interactive: false,
  }

  function requestFrame() {
    if (!destroyed && !frame && !document.hidden) {
      frame = window.requestAnimationFrame(tick)
    }
  }

  function pointInScope(x, y) {
    return scopes.some((element) => {
      const rect = element.getBoundingClientRect()
      return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
    })
  }

  function refreshPointerScope() {
    const active = pointer.hasPosition &&
      pointer.inViewport &&
      pointInScope(pointer.targetX, pointer.targetY)

    pointer.targetStrength = active ? 1 : 0

    const element = active
      ? document.elementFromPoint(pointer.targetX, pointer.targetY)
      : null

    pointer.interactive =
      element instanceof Element && element.closest('a, button, input') !== null
  }

  // The site shell may be mounted after this module loads, or remounted by HMR.
  // Only scope additions/removals trigger a new query, never unrelated content.
  const scopeResizeObserver = new ResizeObserver(requestFrame)
  function refreshScopes() {
    const next = Array.from(document.querySelectorAll(SCOPE_SELECTOR))
    if (next.length === scopes.length && next.every((element, i) => element === scopes[i])) {
      return
    }

    scopeResizeObserver.disconnect()
    scopes = next
    for (const element of scopes) scopeResizeObserver.observe(element)
    refreshPointerScope()
    requestFrame()
  }

  const scopeMutationObserver = new MutationObserver((mutations) => {
    const changed = mutations.some((mutation) =>
      [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
        node instanceof Element &&
        (node.matches(SCOPE_SELECTOR) || node.querySelector(SCOPE_SELECTOR)),
      ),
    )
    if (changed) refreshScopes()
  })
  scopeMutationObserver.observe(document.body, { subtree: true, childList: true })

  function resize() {
    if (destroyed) return
    width = window.innerWidth
    height = window.innerHeight
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR)
    canvas.width = Math.max(1, Math.round(width * dpr))
    canvas.height = Math.max(1, Math.round(height * dpr))
    context.setTransform(dpr, 0, 0, dpr, 0, 0)
    refreshPointerScope()
    requestFrame()
  }

  function draw() {
    context.clearRect(0, 0, width, height)
    context.fillStyle = WHITE

    const active = pointer.strength > 0.003
    const reachSquared = REACH * REACH

    for (const element of scopes) {
      const rect = element.getBoundingClientRect()
      if (!rect.width || !rect.height ||
        rect.right <= 0 || rect.left >= width ||
        rect.bottom <= 0 || rect.top >= height) continue

      // Keep the grid in each section's coordinate space. The Footer scrolls
      // with its dots; the sticky Header keeps its own aligned grid.
      context.save()
      context.beginPath()
      const radius = Math.max(0, parseFloat(getComputedStyle(element).borderTopLeftRadius) || 0)
      if (typeof context.roundRect === 'function') {
        context.roundRect(rect.left, rect.top, rect.width, rect.height, radius)
      } else {
        context.rect(rect.left, rect.top, rect.width, rect.height)
      }
      context.clip()

      const firstColumn = Math.max(0, Math.floor((-rect.left - GAP / 2 - PUSH) / GAP))
      const lastColumn = Math.min(Math.ceil(rect.width / GAP),
        Math.ceil((width - rect.left + PUSH) / GAP))
      const firstRow = Math.max(0, Math.floor((-rect.top - GAP / 2 - PUSH) / GAP))
      const lastRow = Math.min(Math.ceil(rect.height / GAP),
        Math.ceil((height - rect.top + PUSH) / GAP))

      for (let row = firstRow; row < lastRow; row++) {
        const y = rect.top + GAP / 2 + row * GAP
        for (let column = firstColumn; column < lastColumn; column++) {
          const x = rect.left + GAP / 2 + column * GAP
          let movedX = x
          let movedY = y
          let influence = 0

          if (active) {
            const dx = x - pointer.x
            const dy = y - pointer.y
            const distanceSquared = dx * dx + dy * dy

            if (distanceSquared < reachSquared) {
              const distance = Math.sqrt(distanceSquared)
              const falloff = 1 - distance / REACH
              influence = falloff * falloff * (3 - 2 * falloff) * pointer.strength

              if (distance > 0.001) {
                const displacement = PUSH * influence / distance
                movedX += dx * displacement
                movedY += dy * displacement
              }
            }
          }

          const radius = DOT_DIAMETER / 2 +
            (pointer.interactive ? 3.4 : 2.8) * influence

          context.globalAlpha = 0.18 + 0.74 * influence
          context.beginPath()
          context.arc(movedX, movedY, radius, 0, Math.PI * 2)
          context.fill()
        }
      }

      context.restore()
    }

    context.globalAlpha = 1
  }

  function tick() {
    frame = 0
    if (destroyed) return

    pointer.x += (pointer.targetX - pointer.x) * 0.28
    pointer.y += (pointer.targetY - pointer.y) * 0.28
    pointer.strength += (pointer.targetStrength - pointer.strength) * 0.22

    const moving =
      Math.abs(pointer.x - pointer.targetX) > 0.12 ||
      Math.abs(pointer.y - pointer.targetY) > 0.12 ||
      Math.abs(pointer.strength - pointer.targetStrength) > 0.004

    if (!moving) {
      pointer.x = pointer.targetX
      pointer.y = pointer.targetY
      pointer.strength = pointer.targetStrength
    }

    draw()
    // One rAF loop; stop completely when the pointer and overlay settle.
    if (moving) requestFrame()
  }

  /** @param {PointerEvent} event */
  function onPointerMove(event) {
    if (event.pointerType === 'touch') return

    pointer.targetX = event.clientX
    pointer.targetY = event.clientY

    if (!pointer.hasPosition) {
      pointer.x = pointer.targetX
      pointer.y = pointer.targetY
      pointer.hasPosition = true
    }

    pointer.inViewport = true
    refreshPointerScope()
    // Pointer motion in the body never repaints the header/footer grid.
    if (pointer.targetStrength > 0 || pointer.strength > 0.003) requestFrame()
  }

  /** @param {PointerEvent} event */
  function onPointerOut(event) {
    if (event.relatedTarget !== null) return
    pointer.inViewport = false
    refreshPointerScope()
    requestFrame()
  }

  function onBlur() {
    pointer.inViewport = false
    refreshPointerScope()
    requestFrame()
  }

  function onScroll() {
    refreshPointerScope()
    requestFrame()
  }

  function onVisibilityChange() {
    if (document.hidden) {
      window.cancelAnimationFrame(frame)
      frame = 0
    } else {
      requestFrame()
    }
  }

  window.addEventListener('pointermove', onPointerMove, { passive: true })
  window.addEventListener('pointerout', onPointerOut, { passive: true })
  window.addEventListener('blur', onBlur)
  window.addEventListener('scroll', onScroll, { passive: true, capture: true })
  window.addEventListener('resize', resize, { passive: true })
  window.visualViewport?.addEventListener('resize', resize, { passive: true })
  document.addEventListener('visibilitychange', onVisibilityChange)

  resize()
  refreshScopes()

  return () => {
    destroyed = true
    window.cancelAnimationFrame(frame)
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerout', onPointerOut)
    window.removeEventListener('blur', onBlur)
    window.removeEventListener('scroll', onScroll, true)
    window.removeEventListener('resize', resize)
    window.visualViewport?.removeEventListener('resize', resize)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    scopeMutationObserver.disconnect()
    scopeResizeObserver.disconnect()
    scopes = []
    layer.remove()
  }
}

const destroy = mountDotGridWarp()
if (import.meta.hot) import.meta.hot.dispose(destroy)
